import { FC, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Form, Modal } from 'antd'
import { FormProps, useForm, useWatch } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { memo } from 'react'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sclasses from '../SetCourseClassDirectionModal/index.module.css'
import spclasses from '../SetCourseClassDirectionModal/purchase.module.css'
import cn from 'classnames'
import { Lead, RequestCreateLeadPaymentLink } from '@/api/types/crm.types'
import { IcoRemove } from '@/utils/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import Button from '@UI/New/Button'
import { crmApi } from '@/api/queries/crm'
import { isError } from '@/helpers/checkQuery'
import { classApi, directionAPI } from '@/api'
import { ICustomClass } from '../SetCourseClassDirectionModal/SetClassModalBody'
import { useAsync, useAsyncRetry } from 'react-use'
import { InputNew } from '@UI/InputNew'
import { ICustomCourse } from '../SetCourseClassDirectionModal/SetCourseModalBody'
import {
  getLeadField,
  getLeadFieldValue,
} from '@pages/Admin/CRM/utils/leadFields'
import { useGetExtraPurchaseFields } from '@pages/Admin/PlatformSettings2/DirectorySettings/components/ExtraPurchaseFields/utils'
import ClassFields from './ClassFields'
import CourseFields from './CourseFields'
import { AdditionalCoursesField } from './components/SelectAdditionalCourses/SelectAdditionalCourses'
import UserFields from './UserFields'
import IsPromoCheckbox from './IsPromoCheckbox'
import PaymentPeriodCheckbox from './components/PaymentPeriodCheckbox/PaymentPeriodCheckbox'
import CoursePriceView from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/CoursePriceView/CoursePriceView'
import ClassPriceView from '@pages/CoursesCatalog/modals/ClassEnrollmentModal/components/ClassPriceView/ClassPriceView'
import ClassPriceViewWrapper from './ClassPriceViewWrapper'

interface CreateLinkModalProps {
  lead: Lead
  excludedClassIds: number[]
  excludedDirectionIds: number[]
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

type FieldType = Omit<RequestCreateLeadPaymentLink, 'lead_id'>

const initialValues = {
  type: 'course',
  course_id: null,
  tariff_id: null,
  class_id: null,
  price: null,
  discount: null,
  enroll_date: null,
  is_promo_enabled: true,
  value: null,
} as const

const CreateLinkModal: FC<CreateLinkModalProps> = ({
  lead,
  excludedClassIds = [],
  excludedDirectionIds = [],
  open,
  onClose,
  onSuccess,
}) => {
  // ----- Loading Data -----

  const classesState = useAsyncRetry(async () => {
    return (
      ((await classApi.index()).data.data ?? []) as ICustomClass[]
    ).filter((item) => {
      return !excludedClassIds.includes(item.id)
    })
  }, [excludedClassIds])

  const coursesState = useAsync(async () => {
    const res = await directionAPI.getAll({
      filter:
        excludedDirectionIds?.length > 0
          ? [`[direction_id][nin][]=${excludedDirectionIds}`]
          : [],
      expand: [
        'directionTariffs',
        'directionTariffs.tariffPeriods',
        'directionTariffs.tariffWeeks',
        'directionTariffs.tariffDays',
      ],
    })
    const dirtyData = (res?.data?.data as ICustomCourse[]) ?? []
    return dirtyData
      .filter((item) => !!item?.view)
      .map((item) => {
        return {
          ...item,
          directionTariffs: item.directionTariffs?.filter(
            (tariff) => !!tariff.view,
          ),
        }
      })
  }, [excludedDirectionIds])

  // ----- Other -----

  const addAlert = useAddAlert()
  const [form] = useForm()
  const [submitting, setSubmitting] = useState(false)

  const typeWatch = useWatch('type', form) as FieldType['type']
  const courseIdWatch = useWatch('course_id', form) as number | null
  const classIdWatch = useWatch('class_id', form) as number | null
  const selectedClass = classesState.value?.find(
    (item) => item.id === classIdWatch,
  )
  const priceWatch = useWatch('price', form) as number | null
  const tariffWatch = useWatch('tariff_id', form) as number | null
  const selectedTariff = coursesState.value
    ?.find((course) => course.id === courseIdWatch)
    ?.directionTariffs?.find((tariff) => tariff.id === tariffWatch)
  const valueWatch = useWatch('value', form) as number | null

  useEffect(() => form.setFieldsValue({ tariff_id: null }), [courseIdWatch])
  useEffect(
    () =>
      form.setFieldsValue({
        price: selectedClass?.amount ?? null,
        first_price: selectedClass?.amount ?? null,
      }),
    [selectedClass?.id],
  )
  useEffect(() => (!open ? form.resetFields() : undefined), [open])

  // Get tariffs for selected course
  const selectedCourse = coursesState.value?.find(
    (course) => course.id === courseIdWatch,
  )

  const handlerSubmit: FormProps<FieldType>['onFinish'] = (values) => {
    setSubmitting(true)
    crmApi
      .createLeadPaymentLink({
        ...values,
        price: values.price ? Number(values.price) : null,
        first_price: values.first_price ? Number(values.first_price) : null,
        discount: values.discount ? Number(values.discount) : null,
        lead_id: lead.id,
      })
      .then((res) => {
        if (isError(res)) throw new Error()

        addAlert({
          title: 'Успешно',
          text: 'Ссылка успешно создана',
          type: 'success',
        })
        onSuccess?.()
      })
      .catch((error) => {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось создать ссылку',
          type: 'error',
        })
      })
      .finally(() => setSubmitting(false))
  }

  const handlerSubmitFailed: FormProps<FieldType>['onFinishFailed'] = (
    errorInfo,
  ) => {
    const errors = errorInfo.errorFields.map((field) => {
      const fieldName = Array.isArray(field.name) ? field.name[0] : field.name
      const fieldLabels: Record<string, string> = {
        type: 'Тип',
        class_id: 'Класс',
        course_id: 'Курс',
        tariff_id: 'Тариф',
        price: 'Цена',
        discount: 'Скидка',
      }

      const label = fieldLabels[fieldName as string] || fieldName
      return `${label}: ${field.errors.join(', ')}`
    })

    addAlert({
      title: 'Ошибка валидации',
      text: errors,
      type: 'error',
    })
  }

  const filterSort = useCallback((optionA: any, optionB: any) => {
    return (optionA?.label ?? '')
      .toLowerCase()
      .localeCompare((optionB?.label ?? '').toLowerCase())
  }, [])

  // Валидация скидки относительно цены
  const validateDiscount = (_: any, value: number) => {
    if (!value || value === 0) return Promise.resolve()

    const price = form.getFieldValue('price')
    if (!price || price === 0) {
      return Promise.reject(new Error('Сначала необходимо указать цену'))
    }

    if (Number(value) > Number(price)) {
      return Promise.reject(new Error('Скидка не может быть больше цены'))
    }
    return Promise.resolve()
  }

  return (
    <Modal
      open={open}
      onOk={onSuccess}
      onCancel={onClose}
      footer={null}
      title={
        <div className={mclasses.modalHeader}>
          <p className={mclasses.modalHeaderTitle}>
            Создание ссылки на покупку
          </p>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
      }
      centered
      maskClosable
      className={cn(
        amclasses.modal,
        sclasses.rubberLayoutMinimized,
        sclasses.disableAdaptRem,
      )}
    >
      <Form
        noValidate
        form={form}
        initialValues={initialValues}
        onFinish={handlerSubmit}
        onFinishFailed={handlerSubmitFailed}
        style={{ marginTop: '-32px' }}
      >
        <div className={sclasses.contentWrapper}>
          <div
            className={cn(sclasses.modalBlock, 'custom-scroll')}
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
          >
            <UserFields lead={lead} />
            <div style={{ paddingBottom: '6px' }} />
            <FormItem<FieldType> name={'type'} noStyle>
              <TabType />
            </FormItem>
            {typeWatch === 'class' && (
              <>
                <ClassFields
                  form={form}
                  classesState={classesState}
                  filterSort={filterSort}
                  validateDiscount={validateDiscount}
                  priceWatch={priceWatch}
                />
                <div>
                  <FormItem<FieldType> name="value" noStyle>
                    <PaymentPeriodCheckbox type={typeWatch} />
                  </FormItem>
                  {valueWatch && (
                    <div style={{ paddingLeft: '8px', marginBottom: '20px' }}>
                      <ClassPriceViewWrapper form={form} />
                    </div>
                  )}
                </div>
              </>
            )}
            {typeWatch === 'course' && (
              <>
                <CourseFields
                  form={form}
                  selectedCourse={selectedCourse}
                  coursesState={coursesState}
                  filterSort={filterSort}
                />
                {tariffWatch && selectedTariff?.type !== 'free' && (
                  <div>
                    <FormItem<FieldType> name="value" noStyle>
                      <PaymentPeriodCheckbox
                        type={typeWatch}
                        courseTariff={selectedTariff}
                      />
                    </FormItem>
                    {valueWatch && (
                      <div style={{ paddingLeft: '8px', marginBottom: '20px' }}>
                        <CoursePriceView
                          tariff={selectedTariff}
                          value={valueWatch}
                          withDemo={false}
                        />
                      </div>
                    )}
                  </div>
                )}

                {courseIdWatch && (
                  <AdditionalCoursesField
                    userCourses={excludedDirectionIds}
                    isFirstFree={selectedTariff?.type === 'free'}
                    currentCourseId={courseIdWatch}
                    coursesState={coursesState}
                  />
                )}
              </>
            )}
            <FormItem<FieldType> name="is_promo_enabled" noStyle>
              <IsPromoCheckbox type={typeWatch} />
            </FormItem>
          </div>

          <div className={sclasses.footerActions}>
            <Button
              type="submit"
              color="secondary"
              className={sclasses.submitButton}
              disabled={submitting}
              isLoading={submitting}
            >
              Сохранить
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

const TabType = ({
  value,
  onChange,
}: {
  value?: string
  onChange?: (value: string) => void
}) => (
  <div className={sclasses.selectTypeWrapper}>
    <button
      type="button"
      className={
        value === 'course' ? sclasses.tabButtonActive : sclasses.tabButton
      }
      onClick={() => onChange?.('course')}
    >
      Курс
    </button>
    <button
      type="button"
      className={
        value === 'class' ? sclasses.tabButtonActive : sclasses.tabButton
      }
      onClick={() => onChange?.('class')}
    >
      Класс/Траектория
    </button>
  </div>
)

export default CreateLinkModal
